/**
 * The plugin page's header action, its More actions menu, and the confirmation
 * they open.
 *
 * A capability is removed from its own page (Feature 87, D1): Uninstall, and
 * Uninstall and delete while installed; Delete plugin once it is not. A tool
 * is never installed on its own, so its Delete waits until no capability
 * carries it, and the menu says so before any click (D2). The infra plugin
 * is released like any capability but never uninstalled or deleted; its menu
 * holds Reinstall newest, and Disable or Enable (D4, D6).
 *
 * An installed capability plugin, the infra plugin included, offers Restart
 * server: it drains, then a fresh server starts (D12, D13). A disabled one is
 * started with Enable instead, and a restart under way holds the item.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import { deleteCatalogPlugin } from '@optum/api-client/endpoints/catalog';
import {
  listPluginServers,
  restartPluginServer,
  uninstallPlugin,
} from '@optum/api-client/endpoints/plugins';
import type { CatalogEntryOut, CatalogKind } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { Button } from '../../ui/Button.js';
import { useConfirm } from '../../ui/Confirm.js';
import type { ShapeMenuItem } from '../../ui/shapes.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { UninstallConfirmDialog } from './UninstallConfirmDialog.js';
import { useInfraPluginActions } from './useInfraPluginActions.js';

type Removal = 'uninstall' | 'delete';

interface PluginActions {
  action: ReactNode;
  menu: ShapeMenuItem[] | undefined;
  dialog: ReactNode;
}

function message(err: unknown): string {
  return err instanceof ApiError ? err.message : String(err);
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** What the page for `name` offers; empty until its entry has loaded. */
export function usePluginActions(
  kind: CatalogKind,
  name: string,
  entry: CatalogEntryOut | undefined,
): PluginActions {
  const navigate = useNavigate();
  const { isPlatformAdmin } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { reinstall, disable, enable } = useInfraPluginActions(name);
  const [removal, setRemoval] = useState<Removal | null>(null);
  const runs = kind === 'capabilities' && entry?.installed_version != null;
  // The Execution server card polls this list; the menu reads the same cache.
  const servers = useQuery({
    queryKey: qk.pluginServers(),
    queryFn: listPluginServers,
    enabled: runs,
  });

  const leave = (done: string) => {
    toast.success(done);
    void qc.invalidateQueries({ queryKey: ['catalog'] });
    navigate('/plugins', { replace: true });
  };

  const del = useMutation({
    mutationFn: () => deleteCatalogPlugin({ kind, name }),
    onSuccess: () => leave(`Deleted ${name}`),
    onError: (err) => toast.error('Delete plugin failed', message(err)),
  });

  const uninstall = useMutation({
    mutationFn: async ({ cascade, andDelete }: { cascade: boolean; andDelete: boolean }) => {
      await uninstallPlugin({ name, cascade });
      if (andDelete) await deleteCatalogPlugin({ kind, name });
      return andDelete;
    },
    onSuccess: (deleted) => {
      setRemoval(null);
      if (deleted) {
        leave(`Uninstalled and deleted ${name}`);
        return;
      }
      toast.success(`Uninstalled ${name}`);
    },
    onError: (err, { andDelete }) =>
      toast.error(andDelete ? 'Uninstall and delete failed' : 'Uninstall failed', message(err)),
    // Either step may have landed before a failure, so the page reads again.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['catalog'] });
      void qc.invalidateQueries({ queryKey: qk.plugins() });
    },
  });

  const restart = useMutation({
    mutationFn: () => restartPluginServer(name),
    onSuccess: () => {
      toast.success(`Restarting ${name}`, 'It drains first; the Execution server card follows it.');
      void qc.invalidateQueries({ queryKey: qk.pluginServers() });
      void qc.invalidateQueries({ queryKey: ['catalog'] });
    },
    onError: (err) => toast.error('Restart failed', message(err)),
  });

  if (entry === undefined) return { action: null, menu: undefined, dialog: null };

  const restartable = runs && entry.installed_status !== 'disabled';
  const underWay =
    restart.isPending || servers.data?.some((s) => s.dist === entry.name && s.restarting) === true;
  const restartItems: ShapeMenuItem[] = restartable
    ? [
        {
          icon: 'restart_alt',
          label: 'Restart server…',
          sub: underWay ? 'A restart is under way' : 'Drain its tasks, then start a fresh server',
          disabled: underWay,
          onSelect: () => {
            void confirm({
              title: `Restart the server of ${entry.name}?`,
              description:
                'Its tasks stop being claimed and the running ones finish, up to the execution timeout; a task still running then ends as a timeout does. Then a fresh server starts and claims resume.',
              confirmLabel: 'Restart server',
              tone: 'warn',
            }).then((ok) => {
              if (ok) restart.mutate();
            });
          },
        },
      ]
    : [];

  const versions = plural(entry.versions.length, 'version', 'versions');
  const deletePlugin = (blocked?: string): ShapeMenuItem => ({
    icon: 'delete',
    label: del.isPending ? 'Deleting…' : 'Delete plugin',
    sub: blocked ?? `Removes every archive and all ${versions}`,
    tone: 'danger',
    disabled: del.isPending || blocked !== undefined,
    onSelect: () => {
      void confirm({
        title: `Delete ${entry.name}?`,
        description: `Permanently removes every published archive from the index and drops all ${versions}. This cannot be undone.`,
        confirmLabel: 'Delete plugin',
        tone: 'danger',
      }).then((ok) => {
        if (ok) del.mutate();
      });
    },
  });

  const release = (
    <Button
      variant="secondary"
      icon="rocket_launch"
      onClick={() => navigate(`/plugins/${kind}/${encodeURIComponent(entry.name)}/release`)}
    >
      Release
    </Button>
  );

  const dialog = (
    <UninstallConfirmDialog
      open={removal !== null}
      onOpenChange={(open) => {
        if (!open) setRemoval(null);
      }}
      name={entry.name}
      pending={uninstall.isPending}
      onConfirm={(cascade) => uninstall.mutate({ cascade, andDelete: removal === 'delete' })}
      deleteVersions={removal === 'delete' ? entry.versions.length : undefined}
    />
  );

  if (entry.is_infra) {
    const infraMenu: ShapeMenuItem[] = [
      {
        icon: 'refresh',
        label: reinstall.isPending ? 'Reinstalling…' : 'Reinstall newest',
        sub: 'Install the newest version its channel holds, as a boot would',
        disabled: reinstall.isPending,
        onSelect: () => reinstall.mutate(),
      },
      ...restartItems,
      entry.installed_status === 'disabled'
        ? {
            icon: 'play_circle',
            label: enable.isPending ? 'Enabling…' : 'Enable',
            sub: 'Start its server; reinstall it if its directory is gone',
            disabled: enable.isPending,
            onSelect: () => enable.mutate(),
          }
        : {
            icon: 'block',
            label: disable.isPending ? 'Disabling…' : 'Disable',
            sub: 'Stop its server and keep it off across boots',
            tone: 'danger',
            disabled: disable.isPending || entry.installed_version === null,
            onSelect: () => {
              void confirm({
                title: `Disable ${entry.name}?`,
                description:
                  'Its server stops and every boot keeps it disabled. Until it is enabled, the documents and records routes answer 409 ingestion_disabled.',
                confirmLabel: 'Disable',
                tone: 'danger',
              }).then((ok) => {
                if (ok) disable.mutate();
              });
            },
          },
    ];
    return { action: release, menu: isPlatformAdmin ? infraMenu : undefined, dialog: null };
  }

  if (kind === 'tools') {
    const carriers = entry.carried_by.length;
    const blocked =
      carriers > 0
        ? `Carried by ${plural(carriers, 'capability', 'capabilities')}; uninstall them first`
        : undefined;
    return { action: release, menu: isPlatformAdmin ? [deletePlugin(blocked)] : undefined, dialog };
  }

  const installed = entry.installed_version !== null;
  const capabilityMenu: ShapeMenuItem[] = installed
    ? [
        ...restartItems,
        {
          icon: 'remove_circle',
          label: 'Uninstall…',
          sub: 'Remove it from this environment; its versions stay in the index',
          disabled: uninstall.isPending,
          onSelect: () => setRemoval('uninstall'),
        },
        {
          icon: 'delete',
          label: 'Uninstall and delete…',
          sub: `Uninstall, then remove every archive and all ${versions}`,
          tone: 'danger',
          disabled: uninstall.isPending,
          onSelect: () => setRemoval('delete'),
        },
      ]
    : [deletePlugin()];
  return { action: release, menu: isPlatformAdmin ? capabilityMenu : undefined, dialog };
}
